import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Droplets,
  Pencil,
  Plus,
  Trash2
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Control, UseFormSetValue, useForm, useWatch } from "react-hook-form";
import { useGetWaterSourceType } from "../../../api/static-data";
import { Badge } from "../../../components/ui/common/badge";
import { Button } from "../../../components/ui/common/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "../../../components/ui/common/collapsible";
import { Form } from "../../../components/ui/common/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../components/ui/common/tooltip";
import { FormSelect } from "../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../components/ui/Input/FormUnitInput/FormUnitInput";
import { PAGINATION_ITEMS_PER_PAGE, WATER_UNIT_TYPES_OPTIONS } from "../../../config/constants";
import { MEASUREMENT_TYPES } from "../../../config/enums/MeasurementTypes";
import { validateWaterSourceAmount, valueGraterThanZero } from "../../../utils/utils";

interface WaterSourceData {
  WaterSource: number | string;
  WaterAmount: number | string;
  waterUnit?: string | number;
}

interface WaterSourceRecord {
  WaterSource: number;
  WaterAmount: number;
  waterUnit?: string | number;
}

interface ProcessWaterSourceProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  lockStatus?: boolean;
}

const ProcessWaterSource = ({ control, setValue, lockStatus = false }: ProcessWaterSourceProps) => {
  const [waterOpen, setWaterOpen] = useState(true);
  const [waterPage, setWaterPage] = useState(1);
  const [editingWaterId, setEditingWaterId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<(WaterSourceRecord & { index: number }) | null>(
    null
  );
  const initializedRef = useRef(false);

  const waterSourceData = useWatch({ control, name: "waterSource" });
  const waterValue = useWatch({ control, name: "water" });
  const waterSource = useMemo(() => waterSourceData || [], [waterSourceData]);

  const { data: waterSourceType } = useGetWaterSourceType();

  const defaultWaterSourceType = useMemo(() => {
    return waterSourceType && waterSourceType.length > 0 ? waterSourceType[0].value : "1";
  }, [waterSourceType]);

  // Initialize waterSource array from water value on first load.
  // Uses a ref to avoid race conditions between waterSourceType and waterValue loading asynchronously.
  useEffect(() => {
    if (initializedRef.current) return;
    if (!waterSourceType || waterSourceType.length === 0) return;
    if (waterValue === undefined || waterValue === null) return;

    initializedRef.current = true;

    const hasWaterValue = Number(waterValue) > 0;
    const hasWaterSource = waterSource && waterSource.length > 0;

    // If water value exists but no waterSource array yet, create default entry with Tap water
    if (hasWaterValue && !hasWaterSource) {
      const defaultRecord: WaterSourceRecord = {
        WaterSource: Number(defaultWaterSourceType),
        WaterAmount: Number(waterValue)
      };
      setValue("waterSource", [defaultRecord], { shouldDirty: false, shouldValidate: false });
    }
  }, [waterValue, waterSource, waterSourceType, defaultWaterSourceType, setValue]);

  // Separate form for add/edit operations (matches original pattern)
  const waterForm = useForm<WaterSourceData>({
    mode: "all",
    defaultValues: {
      WaterSource: defaultWaterSourceType,
      WaterAmount: "",
      waterUnit: MEASUREMENT_TYPES.VOLUME_m3_PER_KG
    }
  });

  const selectedWaterSource = waterForm.watch("WaterSource");

  const totalWater = useMemo(() => {
    if (!waterSource || !Array.isArray(waterSource)) return waterValue || 0;
    let totalWaterAmount = 0;
    waterSource.reduce((acc: number, item: WaterSourceRecord) => {
      const amount = Number(item.WaterAmount) || 0;
      totalWaterAmount += amount;
      return acc + amount;
    }, 0);
    return totalWaterAmount;
  }, [waterSource, waterValue]);

  const waterPages = Math.ceil(waterSource.length / PAGINATION_ITEMS_PER_PAGE);
  const paginatedWater = waterSource.slice(
    (waterPage - 1) * PAGINATION_ITEMS_PER_PAGE,
    waterPage * PAGINATION_ITEMS_PER_PAGE
  );

  const handleWaterFormSubmit = waterForm.handleSubmit((data: WaterSourceData) => {
    if (!data.WaterSource) return;
    // Update totalWater with new entry before syncing to main form

    const newRecord: WaterSourceRecord = {
      WaterSource: Number(data.WaterSource),
      WaterAmount: Number(data.WaterAmount) || 0
    };

    if (editingWaterId !== null && editingItem) {
      // Update existing water source (edit mode)
      const updatedWaterSource = waterSource.map((item: WaterSourceRecord, index: number) =>
        index === editingItem.index ? newRecord : item
      );
      setValue("waterSource", updatedWaterSource, { shouldDirty: true, shouldValidate: true });
      setEditingWaterId(null);
      setEditingItem(null);
    } else {
      // Check if water source already exists and update, otherwise add (add mode)
      const existingIndex = waterSource.findIndex(
        (item: WaterSourceRecord) => item.WaterSource === newRecord.WaterSource
      );

      if (existingIndex !== -1) {
        const updatedWaterSource = waterSource.map((item: WaterSourceRecord, index: number) =>
          index === existingIndex
            ? { ...item, WaterAmount: Number(item.WaterAmount) + Number(newRecord.WaterAmount) }
            : item
        );
        setValue("waterSource", updatedWaterSource, { shouldDirty: true, shouldValidate: true });
      } else {
        setValue("waterSource", [...waterSource, newRecord], {
          shouldDirty: true,
          shouldValidate: true
        });
        setWaterPage(Math.ceil((waterSource.length + 1) / PAGINATION_ITEMS_PER_PAGE));
      }

      // Reset editing state in add mode as well
      setEditingWaterId(null);
      setEditingItem(null);
    }
    setValue("water", totalWater + Number(data.WaterAmount || 0), {
      shouldDirty: true,
      shouldValidate: true
    });
    // Reset form
    waterForm.reset({
      WaterSource: defaultWaterSourceType,
      WaterAmount: "",
      waterUnit: MEASUREMENT_TYPES.VOLUME_m3_PER_KG
    });
  });

  const handleDeleteWaterSource = (index: number): void => {
    const updatedWaterSource = waterSource.filter((_: WaterSourceRecord, i: number) => i !== index);
    setValue("waterSource", updatedWaterSource, { shouldDirty: true, shouldValidate: true });

    const newLen = updatedWaterSource.length;
    const newPages = Math.ceil(newLen / PAGINATION_ITEMS_PER_PAGE);
    if (waterPage > newPages && newPages > 0) setWaterPage(newPages);
  };

  const renderWaterRow = (
    item: WaterSourceRecord,
    index: number,
    isDraft = false
  ): JSX.Element | null => {
    if (isDraft) {
      return (
        <Form {...waterForm}>
          <form key="draft-row" className="tw-space-y-1" onSubmit={handleWaterFormSubmit}>
            <div className="tw-grid tw-grid-cols-[1fr_250px_auto] tw-gap-2 tw-items-center">
              <FormSelect
                control={waterForm.control}
                name="WaterSource"
                placeholder="Select water source"
                options={waterSourceType}
                disabled={lockStatus}
                required
                className="!tw-h-10"
              />
              <FormUnitInput
                control={waterForm.control}
                name="WaterAmount"
                unitOptions={WATER_UNIT_TYPES_OPTIONS}
                unitName="waterUnit"
                placeholder="0.00"
                enableLimitedDecimal
                viewDecimalNumber={12}
                disabled={lockStatus}
                required="Water amount is required"
                validate={{
                  valueCheck: (v: string | number) =>
                    valueGraterThanZero(v) || "Water Amount must be greater than 0",
                  maxCheck: (v: string | number) => validateWaterSourceAmount(v)
                }}
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="submit"
                      variant="default"
                      size="icon"
                      className="tw-w-10 tw-h-10 tw-mt-[5px]"
                      disabled={!selectedWaterSource || lockStatus}
                    >
                      <Plus className="tw-h-3.5 tw-w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="tw-text-xs">Update changes</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </form>
        </Form>
      );
    }

    const fieldIndex = index;

    // Get water source label from waterSourceType
    const waterSourceLabel =
      waterSourceType?.find((s: any) => String(s.value) === String(item?.WaterSource))?.text ||
      item?.WaterSource ||
      "—";

    const itemId = `water-${item.WaterSource}-${index}`;
    const isReadOnly = editingWaterId !== itemId || lockStatus;

    // Read-only view
    if (isReadOnly) {
      return (
        <div
          key={itemId}
          className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-2 tw-rounded-md tw-bg-muted/30 tw-border tw-border-border/50"
        >
          <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-text-xs">
            <span className="tw-font-medium tw-text-foreground">{waterSourceLabel}</span>
            <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">
              {item?.WaterAmount || 0} m³/kg
            </span>
          </div>
          {!lockStatus && (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
                      onClick={() => {
                        // Populate form with item data (edit mode)
                        waterForm.reset({
                          WaterSource: String(item.WaterSource),
                          WaterAmount: String(item.WaterAmount),
                          waterUnit: item.waterUnit || MEASUREMENT_TYPES.VOLUME_m3_PER_KG
                        });
                        setEditingWaterId(itemId);
                        setEditingItem({ ...item, index: fieldIndex });
                      }}
                    >
                      <Pencil className="tw-h-3 tw-w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="tw-text-xs">Edit</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="ghost"
                size="icon"
                className="tw-h-7 tw-w-7 tw-text-destructive hover:tw-text-destructive"
                onClick={() => handleDeleteWaterSource(fieldIndex)}
              >
                <Trash2 className="tw-h-3 tw-w-3" />
              </Button>
            </>
          )}
        </div>
      );
    }

    // Edit mode - Use same waterForm with Form wrapper (matches original pattern)
    return (
      <Form {...waterForm}>
        <form
          key={itemId}
          onSubmit={handleWaterFormSubmit}
          className="tw-border tw-border-primary/30 tw-rounded-md tw-p-2 tw-bg-primary/5 tw-space-y-2"
        >
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-flex-[2]">
              <FormSelect
                control={waterForm.control}
                name="WaterSource"
                placeholder="Select water source"
                options={waterSourceType}
                disabled={lockStatus}
                required
              />
            </div>
            <div className="tw-flex-[2]">
              <FormUnitInput
                control={waterForm.control}
                name="WaterAmount"
                unitOptions={WATER_UNIT_TYPES_OPTIONS}
                unitName="waterUnit"
                placeholder="0.00"
                enableLimitedDecimal
                viewDecimalNumber={12}
                disabled={lockStatus}
                required="Water amount is required"
                validate={{
                  valueCheck: (v: string | number) =>
                    valueGraterThanZero(v) || "Water Amount must be greater than 0",
                  maxCheck: (v: string | number) => validateWaterSourceAmount(v)
                }}
              />
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="submit"
                    variant="default"
                    size="icon"
                    className="tw-h-[42px] tw-w-[42px]"
                    disabled={lockStatus}
                  >
                    <Check className="tw-h-3.5 tw-w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="tw-text-xs">Update changes</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </form>
      </Form>
    );
  };

  const renderPagination = (): JSX.Element | null => {
    if (waterPages <= 1) return null;
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-pt-2">
        <Button
          variant="ghost"
          size="icon"
          className="tw-h-7 tw-w-7"
          disabled={waterPage <= 1}
          onClick={() => setWaterPage(waterPage - 1)}
        >
          <ChevronLeft className="tw-h-3.5 tw-w-3.5" />
        </Button>
        <span className="tw-text-[10px] tw-text-muted-foreground tw-font-mono">
          {waterPage} / {waterPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="tw-h-7 tw-w-7"
          disabled={waterPage >= waterPages}
          onClick={() => setWaterPage(waterPage + 1)}
        >
          <ChevronRight className="tw-h-3.5 tw-w-3.5" />
        </Button>
      </div>
    );
  };

  return (
    <Collapsible open={waterOpen} onOpenChange={setWaterOpen}>
      <div className="tw-border tw-border-dashed tw-border-accent/50 tw-rounded-md">
        <CollapsibleTrigger className="tw-flex tw-items-center tw-justify-between tw-w-full tw-p-1 hover:tw-bg-accent/5 tw-transition-colors tw-rounded-md">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Droplets className="tw-h-3.5 tw-w-3.5 tw-text-accent" />
            <span className="tw-text-xs tw-font-medium tw-text-foreground">Water Sources</span>
            {waterSource.length > 0 && (
              <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
                {waterSource.length}
              </Badge>
            )}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-[10px] tw-font-mono tw-text-muted-foreground">
              Total: {totalWater.toFixed(2)} m³/kg
            </span>
            {waterOpen ? (
              <ChevronUp className="tw-h-3 tw-w-3 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-3 tw-w-3 tw-text-muted-foreground" />
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="tw-px-3 tw-pb-3 tw-space-y-2">
          {paginatedWater.map((item: any, paginatedIndex: number) => {
            const actualIndex = (waterPage - 1) * PAGINATION_ITEMS_PER_PAGE + paginatedIndex;
            return renderWaterRow(item, actualIndex, false);
          })}
          {renderPagination()}
          {waterSource.length > 0 && <div className="tw-border-t tw-border-dashed tw-border-border" />}
          <div>
            <p className="tw-text-[10px] tw-text-muted-foreground tw-mb-1">Add new water source</p>
            {renderWaterRow(null as any, 0, true)}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default ProcessWaterSource;
